import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

/**
 * AI Usage Credits gate for Screen AI / Media AI.
 * @param {{ autoPromptOnMount?: boolean }} options
 */
export function useAiCreditsGate({ autoPromptOnMount = true } = {}) {
  const user = useSelector((state) => state.user);
  const [payOpen, setPayOpenState] = useState(false);
  const userOpenedPayRef = useRef(false);
  const dismissedAutoPayRef = useRef(false);

  const creditsKnown = Boolean(user?.userTokenDetails);

  const remaining = useMemo(() => {
    const d = user?.userTokenDetails;
    if (!d) return 0;
    return Math.max(
      0,
      Number(d.tokenLimit || 0) - Number(d.totalTokensUsed || 0)
    );
  }, [user]);

  /** Unknown balance after refresh → do not block UI until profile sync. */
  const canUse = !creditsKnown || remaining > 0;

  const setPayOpen = useCallback((open) => {
    setPayOpenState(open);
    if (!open) {
      if (!userOpenedPayRef.current) {
        dismissedAutoPayRef.current = true;
      }
      userOpenedPayRef.current = false;
    }
  }, []);

  const promptBuy = useCallback(() => {
    userOpenedPayRef.current = true;
    dismissedAutoPayRef.current = false;
    setPayOpenState(true);
  }, []);

  useEffect(() => {
    if (!autoPromptOnMount || !creditsKnown) return;

    if (remaining > 0) {
      if (!userOpenedPayRef.current) {
        setPayOpenState(false);
      }
      dismissedAutoPayRef.current = false;
      return;
    }

    if (!userOpenedPayRef.current && !dismissedAutoPayRef.current) {
      setPayOpenState(true);
    }
  }, [autoPromptOnMount, creditsKnown, remaining]);

  return {
    remaining,
    canUse,
    creditsKnown,
    payOpen,
    setPayOpen,
    promptBuy,
  };
};
